import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Calendar as CalendarIcon, List, LayoutGrid, Settings2, ChevronDown, Paperclip, X, Layers, Repeat } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { commsAPI } from '../lib/communicationsAPI';
import { taskTemplatesAPI, taskExtrasAPI, TASK_STATUS, REPEAT_LABELS } from '../lib/taskTemplatesAPI';
import { DeadlineFields } from '../components/tasks/DeadlineFields';
import { TaskListView, DueDates } from '../components/tasks/TaskListView';
import { BulkCreateDialog } from '../components/tasks/BulkCreateDialog';
import { RecurringDialog } from '../components/tasks/RecurringDialog';
import { periodFromKey, currentKey, buildTaskTitle } from '../lib/taxPeriods';
import { TemplateCombobox } from '../components/tasks/TemplateCombobox';
import { ClientCombobox } from '../components/tasks/ClientCombobox';
import { PeriodPicker } from '../components/tasks/PeriodPicker';
import { ChecklistEditor, TaskProgress } from '../components/tasks/ChecklistEditor';
import { TaskDetailDialog } from '../components/tasks/TaskDetailDialog';
import { TaskTemplatesAdmin } from '../components/tasks/TaskTemplatesAdmin';
import { toast } from 'sonner';
import { parseISO, differenceInCalendarDays } from 'date-fns';
import { tasksAPI } from '../lib/complianceAPI';
import { clientsAPI } from '../lib/clientsAPI';
import { authAPI } from '../lib/api';
import EventCalendar from '../components/calendar/EventCalendar';
import { FilterBar } from '../components/ds/FilterBar';
import { Checkbox } from '../components/ui/checkbox';
import { caAPI, errText } from '../lib/complianceAutoAPI';
import { LodgeToCompleteDialog, needsLodgment, isLodgmentPrompt, taskErr } from '../components/tasks/LodgeToCompleteDialog';

// Map a task's workflow status (todo/in_progress/done) + due date to the
// calendar's timing status: done → completed; otherwise overdue / due soon / pending.
const TASK_STATUS_LABELS = Object.fromEntries(TASK_STATUS.map((s) => [s.id, s.title]));
const taskCalendarStatus = (task) => {
  if (task.status === 'done') return 'completed';
  if (!task.due_at) return 'pending';
  const diff = differenceInCalendarDays(typeof task.due_at === 'string' ? parseISO(task.due_at) : new Date(task.due_at), new Date());
  if (diff < 0) return 'overdue';
  if (diff <= 7) return 'due_soon';
  return 'pending';
};

const priorityColors = {
  low: 'bg-muted text-foreground/90 border-border',
  medium: 'bg-blue-100 text-blue-700 border-blue-200',
  high: 'bg-red-100 text-red-700 border-red-200'
};

const COLUMN_BG = { todo: 'bg-muted/50', in_progress: 'bg-blue-50 dark:bg-blue-950/30', waiting_client: 'bg-amber-50 dark:bg-amber-950/30',
  in_review: 'bg-violet-50 dark:bg-violet-950/30', done: 'bg-green-50 dark:bg-green-950/30' };
const statusColumns = TASK_STATUS.map((s) => ({ ...s, bgColor: COLUMN_BG[s.id] }));
const COMPLETED_OPTS = [{ value: 'today', label: 'Today' }, { value: '7d', label: 'Last 7 days' }, { value: '30d', label: 'Last 30 days' }, { value: 'all', label: 'All' }];
const doneTitle = (c) => (c === 'today' ? 'Done today' : c === 'all' ? 'Done (all)' : `Done (${COMPLETED_OPTS.find((o) => o.value === c)?.label.toLowerCase()})`);
const EMPTY_FORM = { task_template_id: '', client_id: '', client: null, period_key: '', title: '', description: '', status: 'todo', priority: 'medium',
  due_at: '', assignee_id: '', checklist: [], start_date: '', notes: '', files: [], titleTouched: false, descTouched: false,
  deadline: {}, repeats: 'none', repeat_interval_months: 2 };

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban'); // 'list', 'kanban', or 'calendar'
  const [dialogOpen, setDialogOpen] = useState(false);
  const { search: locSearch } = useLocation();
  const nav = useNavigate();
  const urlF = Object.fromEntries(['status', 'due', 'client', 'assignee', 'type', 'completed'].map((k) => [k, new URLSearchParams(locSearch).get(k)]).filter(([, v]) => v));
  const completed = urlF.completed || 'today';
  const hasUrlF = !new URLSearchParams(locSearch).get('new') && Object.keys(urlF).length > 0;
  useEffect(() => { if (hasUrlF) setView('kanban'); }, []); // eslint-disable-line
  const setUrlF = (f) => { const sp = new URLSearchParams(); const tk = new URLSearchParams(locSearch).get('task'); if (tk) sp.set('task', tk); Object.entries(f).forEach(([k, v]) => v && sp.set(k, v)); nav(`/tasks${sp.toString() ? `?${sp}` : ''}`, { replace: true }); };
  const [picked, setPicked] = useState([]);
  const [bulkAssignee, setBulkAssignee] = useState('');
  useEffect(() => {
    const sp = new URLSearchParams(locSearch);
    if (sp.get('new') !== '1') return;
    setDialogOpen(true);
    if (sp.get('client')) setFormData((f) => ({ ...f, client_id: sp.get('client'), client: { id: sp.get('client'), display_name: sp.get('client_name') || 'Client' } }));
    nav('/tasks', { replace: true });
  }, [locSearch]); // eslint-disable-line
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [role, setRole] = useState(null);
  const [meId, setMeId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [adminOpen, setAdminOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [recurOpen, setRecurOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [lodgeFor, setLodgeFor] = useState(null);

  useEffect(() => {
    loadClients();
    loadUsers();
    loadTemplates();
    authAPI.getMe().then((r) => { setRole(r.data.active_membership?.role || r.data.user?.role); setMeId(r.data.user?.id || r.data.id); }).catch(() => {});
  }, []);
  useEffect(() => { loadTasks(); }, [completed]); // eslint-disable-line

  const loadTemplates = () => taskTemplatesAPI.list().then((r) => setTemplates(r.data.templates)).catch(() => {});
  const selectedTemplate = templates.find((t) => t.id === formData.task_template_id);
  const selectedClient = formData.client || clients.find((c) => c.id === formData.client_id);

  // Re-derive auto fields (title stops auto-updating once edited by hand).
  const applyAuto = (next) => {
    const t = templates.find((x) => x.id === next.task_template_id);
    const c = next.client;
    if (t && !next.titleTouched) next.title = buildTaskTitle(t.title_pattern, c?.display_name, t.name, periodFromKey(next.period_key)?.label);
    return next;
  };
  const onTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    setFormData((f) => applyAuto({ ...f, task_template_id: id,
      period_key: t && t.period_type !== 'none' ? currentKey(t.period_type) : '',
      description: t && !f.descTouched ? (t.description || '') : f.description,
      priority: t ? t.default_priority : f.priority,
      checklist: t ? t.checklist.map(({ title, is_required }) => ({ title, is_required })) : [],
      repeats: t && t.period_type !== 'none' ? (t.default_recurrence || 'none') : 'none', deadline: {},
      title: t || f.titleTouched ? f.title : '' }));
  };
  const onClient = (c) => setFormData((f) => applyAuto({ ...f, client: c, client_id: c?.id || '', assignee_id: c?.assigned_accountant_id || '' }));
  const onPeriod = (key) => setFormData((f) => applyAuto({ ...f, period_key: key }));

  useEffect(() => {  // deep link /tasks?task=<id> (from Compliance / Client Deadlines); an older Done task hidden from the board is fetched directly
    const id = new URLSearchParams(window.location.search).get('task');
    if (!id || detail || loading) return;
    const t = tasks.find((x) => x.id === id);
    if (t) setDetail(t); else tasksAPI.get(id).then((r) => setDetail(r.data)).catch(() => toast.error('That task could not be found'));
  }, [tasks, loading]); // eslint-disable-line
  const loadTasks = async () => {
    try {
      const response = await tasksAPI.list({ completed });
      setTasks(response.data);
      setReloadKey((k) => k + 1);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const response = await clientsAPI.list({ status: 'active' });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const loadUsers = async () => {
    try {
      const response = await commsAPI.staff();
      setUsers(response.data.staff);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      toast.error('Please enter a task title');
      return;
    }

    const dl = formData.deadline;
    if (selectedTemplate && dl.deadline_rule_id && !dl.confirmed) {
      toast.error('This deadline rule needs admin verification — tick "I\'ve checked this date" to confirm it');
      return;
    }
    const internal = selectedTemplate ? dl.internal_due_date : formData.due_at;
    const repeats = selectedTemplate && formData.repeats !== 'none' ? formData.repeats : null;
    const body = {
        client_id: formData.client_id || null,
        title: formData.title,
        description: formData.description || null,
        status: formData.status,
        priority: formData.priority,
        due_at: internal ? new Date(internal).toISOString() : null,
        internal_due_date: internal || null,
        statutory_due_date: selectedTemplate ? dl.statutory_due_date || null : null,
        deadline_rule_id: selectedTemplate ? dl.deadline_rule_id || null : null,
        deadline_overridden: !!dl.overridden,
        repeats, repeat_interval_months: repeats === 'custom' ? Number(formData.repeat_interval_months) : null,
        assignee_id: formData.assignee_id || null,
        task_template_id: formData.task_template_id || null,
        period_key: formData.period_key || null,
        checklist: formData.checklist,
        start_date: formData.start_date || null,
        notes: formData.notes || null,
    };
    setSubmitting(true);
    try {
      let res;
      try {
        res = await tasksAPI.create(body);
      } catch (e) {
        const d = e.response?.status === 409 && e.response.data?.detail;
        if (!d?.duplicate || !window.confirm(`${d.message}. Create another one anyway?`)) throw e;
        res = await tasksAPI.create({ ...body, allow_duplicate: true });
      }
      for (const file of formData.files) {
        await taskExtrasAPI.upload(res.data.id, file).catch((e) => toast.error(`${file.name}: ${e.response?.data?.detail || 'upload failed'}`));
      }
      
      toast.success('Task created successfully');
      setDialogOpen(false);
      setFormData(EMPTY_FORM);
      setMoreOpen(false);
      loadTasks();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    if (newStatus === 'done' && needsLodgment(task)) { setLodgeFor(task); return; }
    try {
      await tasksAPI.updateStatus(task.id, newStatus);
      toast.success(newStatus === 'done' ? 'Task marked Done' : 'Task updated');
      loadTasks();
    } catch (error) {
      if (isLodgmentPrompt(error)) setLodgeFor(task);
      else toast.error(taskErr(error));
      loadTasks();
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  const todayIso = new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
  const weekEnd = new Date(Date.now() + 7 * 86400000).toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
  const sydDay = (s) => (s.length <= 10 ? s : new Date(s).toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' }));
  const dueOk = (d) => (urlF.due === 'today' ? d === todayIso : urlF.due === 'week' ? d >= todayIso && d <= weekEnd : d < todayIso);
  const shownTasks = !hasUrlF ? tasks : tasks.filter((t) => (!urlF.client || t.client_id === urlF.client)
    && (!urlF.status || (urlF.status === 'open' ? t.status !== 'done' : t.status === urlF.status))
    && (!urlF.assignee || (urlF.assignee === 'unassigned' ? !t.assignee_id : t.assignee_id === (urlF.assignee === 'me' ? meId : urlF.assignee)))
    && (!urlF.type || (urlF.type === 'compliance') === !!t.compliance_period_id)
    && (!urlF.due || (t.status !== 'done' && t.due_at && dueOk(sydDay(t.due_at)))));
  const taskFilters = [
    { key: 'status', label: 'Status', options: [{ value: 'open', label: 'Open' }, ...statusColumns.map((c) => ({ value: c.id, label: c.title }))] },
    { key: 'assignee', label: 'Assignee', options: [{ value: 'me', label: 'Me' }, { value: 'unassigned', label: 'Unassigned' }, ...users.map((u) => ({ value: u.id, label: u.name || u.email }))] },
    { key: 'client', label: 'Client', options: clients.map((c) => ({ value: c.id, label: c.display_name })) },
    { key: 'due', label: 'Due', options: [{ value: 'today', label: 'Today' }, { value: 'overdue', label: 'Overdue' }, { value: 'week', label: 'Next 7 days' }] },
    { key: 'type', label: 'Type', options: [{ value: 'compliance', label: 'Compliance' }, { value: 'general', label: 'General' }] },
    { key: 'completed', label: 'Show completed', options: COMPLETED_OPTS },
  ];
  const canBulk = ['owner', 'admin', 'accountant'].includes(role);
  const canWrite = !!role && role !== 'read_only';
  const runBulkAssign = async () => {
    try { const r = await caAPI.bulk({ task_ids: picked, action: 'assign', value: bulkAssignee === 'none' ? null : bulkAssignee }); toast.success(`${r.updated} reassigned${r.errors.length ? `, ${r.errors.length} failed` : ''}`); setPicked([]); setBulkAssignee(''); loadTasks(); } catch (e) { toast.error(errText(e)); }
  };
  const tasksByStatus = Object.fromEntries(statusColumns.map((c) => [c.id, shownTasks.filter((t) => t.status === c.id)]));

  // Normalised events for the Google-Calendar-style view.
  const calendarEvents = shownTasks
    .filter(t => t.due_at)
    .map(t => ({
      id: t.id,
      date: typeof t.due_at === 'string' ? parseISO(t.due_at) : new Date(t.due_at),
      title: t.title,
      subtitle: t.client_name || (t.assignee_name ? `@${t.assignee_name}` : ''),
      status: taskCalendarStatus(t),
      stage: TASK_STATUS_LABELS[t.status] || t.status,
    }));

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl" data-testid="tasks-title">
            Tasks
          </h1>
          <p className="text-muted-foreground mt-1">Manage tasks and track progress</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex border rounded-lg p-1">
            <Button 
              variant={view === 'kanban' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setView('kanban')}
              className={view === 'kanban' ? 'bg-primary hover:bg-primary/90' : ''}
            >
              <LayoutGrid className="w-4 h-4 mr-2" />
              Kanban
            </Button>
            <Button 
              variant={view === 'list' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setView('list')}
              className={view === 'list' ? 'bg-primary hover:bg-primary/90' : ''}
            >
              <List className="w-4 h-4 mr-2" />
              List
            </Button>
            <Button 
              variant={view === 'calendar' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setView('calendar')}
              className={view === 'calendar' ? 'bg-primary hover:bg-primary/90' : ''}
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              Calendar
            </Button>
          </div>
          {canWrite && <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="task-bulk-create-btn"><Layers className="w-4 h-4 mr-2" />Bulk create</Button>}
          {canWrite && <Button variant="outline" onClick={() => setRecurOpen(true)} data-testid="task-recurring-btn"><Repeat className="w-4 h-4 mr-2" />Recurring</Button>}
          {['owner', 'admin'].includes(role) && (
            <Button variant="outline" onClick={() => setAdminOpen(true)} data-testid="task-templates-settings-btn">
              <Settings2 className="w-4 h-4 mr-2" />Templates
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className={`bg-primary hover:bg-primary/90 ${canWrite ? '' : 'hidden'}`} data-testid="add-task-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Task
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Task</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="new-task-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Task type / template</Label>
                    <TemplateCombobox templates={templates} value={formData.task_template_id} onChange={onTemplate} entityType={selectedClient?.entity_type} />
                  </div>
                  <div className="space-y-2">
                    <Label>Client / entity</Label>
                    <ClientCombobox value={formData.client_id} label={selectedClient?.display_name} onChange={onClient} />
                  </div>
                </div>

                {selectedTemplate && selectedTemplate.period_type !== 'none' && (
                  <div className="space-y-2" data-testid="task-period-field">
                    <Label>Reporting period</Label>
                    <div className="flex flex-wrap gap-3 items-start">
                      <div className="flex-1 min-w-[220px]"><PeriodPicker periodType={selectedTemplate.period_type} value={formData.period_key} onChange={onPeriod} /></div>
                      <Select value={formData.repeats} onValueChange={(v) => setFormData({...formData, repeats: v})}>
                        <SelectTrigger className="w-40" data-testid="task-repeats-select"><Repeat className="w-3.5 h-3.5 mr-1" /><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(REPEAT_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{k === 'none' ? l : `Repeats ${l.toLowerCase()}`}</SelectItem>)}</SelectContent>
                      </Select>
                      {formData.repeats === 'custom' && (
                        <div className="flex items-center gap-1.5 text-sm">every<Input type="number" min={1} max={36} className="w-16 h-9" value={formData.repeat_interval_months}
                          onChange={(e) => setFormData({...formData, repeat_interval_months: e.target.value})} data-testid="task-repeat-interval" />months</div>)}
                    </div>
                  </div>
                )}
                {selectedTemplate && (
                  <DeadlineFields templateId={formData.task_template_id} periodKey={formData.period_key} value={formData.deadline}
                    onChange={(d) => setFormData((f) => ({ ...f, deadline: d }))} />
                )}

                <div className="space-y-2">
                  <Label htmlFor="title">Task Title *</Label>
                  <Input
                    id="title"
                    placeholder="e.g., Prepare BAS Q1 FY25"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value, titleTouched: true})}
                    required
                    data-testid="task-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Task details..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value, descTouched: true})}
                    rows={2}
                    data-testid="task-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="assignee">Assigned staff</Label>
                    <Select value={formData.assignee_id || 'none'} onValueChange={(value) => setFormData({...formData, assignee_id: value === 'none' ? '' : value})}>
                      <SelectTrigger id="assignee" data-testid="task-assignee-select">
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.name || user.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority</Label>
                    <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                      <SelectTrigger id="priority" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                      <SelectTrigger id="status" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TASK_STATUS.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {!selectedTemplate && (
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="due_at">Due Date</Label>
                    <Input id="due_at" type="date" value={formData.due_at} onChange={(e) => setFormData({...formData, due_at: e.target.value})} data-testid="task-due-input" />
                  </div>)}
                </div>

                {(selectedTemplate || formData.checklist.length > 0) && (
                  <div className="space-y-2" data-testid="task-checklist-field">
                    <Label>Checklist ({formData.checklist.length})</Label>
                    <ChecklistEditor items={formData.checklist} onChange={(c) => setFormData({...formData, checklist: c})} testPrefix="new-task-checklist" />
                  </div>
                )}

                <Collapsible open={moreOpen} onOpenChange={setMoreOpen}>
                  <CollapsibleTrigger asChild>
                    <Button type="button" variant="ghost" size="sm" className="px-0 text-muted-foreground" data-testid="task-more-options">
                      <ChevronDown className={`w-4 h-4 mr-1 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />More options
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-3 pt-2">
                    {!selectedTemplate && formData.checklist.length === 0 && (
                      <div className="space-y-2"><Label>Checklist</Label>
                        <ChecklistEditor items={formData.checklist} onChange={(c) => setFormData({...formData, checklist: c})} testPrefix="new-task-checklist" /></div>
                    )}
                    <div className="space-y-2 max-w-[200px]"><Label htmlFor="start_date">Start date</Label>
                      <Input id="start_date" type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} data-testid="task-start-input" /></div>
                    <div className="space-y-2"><Label htmlFor="notes">Notes</Label>
                      <Textarea id="notes" rows={2} value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} data-testid="task-notes-input" /></div>
                    <div className="space-y-2"><Label>Attachments</Label>
                      <label className="inline-flex items-center gap-1.5 text-sm text-primary cursor-pointer hover:underline">
                        <Paperclip className="w-4 h-4" />Add files
                        <input type="file" multiple className="hidden" onChange={(e) => { setFormData({...formData, files: [...formData.files, ...e.target.files]}); e.target.value = ''; }} data-testid="task-files-input" />
                      </label>
                      {formData.files.map((f, i) => (
                        <div key={`${f.name}-${i}`} className="flex items-center gap-2 text-xs"><span className="truncate">{f.name}</span>
                          <button type="button" onClick={() => setFormData({...formData, files: formData.files.filter((_, j) => j !== i)})} aria-label="Remove file"><X className="w-3 h-3" /></button></div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={submitting} data-testid="task-create-submit">
                    {submitting ? 'Creating...' : 'Create Task'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="tasks-url-filter">
          <FilterBar filters={taskFilters} value={urlF} onChange={setUrlF} viewsKey="tasks" userId={meId} testId="tasks-filter-bar" />
          {hasUrlF && view !== 'list' && <span className="text-xs text-muted-foreground tabular" data-testid="tasks-filter-count">{shownTasks.length} of {tasks.length}</span>}
        </div>
      {canBulk && picked.length > 0 && view === 'kanban' && (
        <div className="sticky top-16 z-20 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-2 shadow-card" data-testid="tasks-bulk-bar">
          <span className="text-sm tabular">{picked.length} compliance task{picked.length === 1 ? '' : 's'} selected</span>
          <Select value={bulkAssignee} onValueChange={setBulkAssignee}>
            <SelectTrigger className="h-8 w-48" data-testid="tasks-bulk-assignee"><SelectValue placeholder="Assign to…" /></SelectTrigger>
            <SelectContent><SelectItem value="none">Unassigned</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" disabled={!bulkAssignee} onClick={runBulkAssign} data-testid="tasks-bulk-assign">Assign</Button>
          <Button size="sm" variant="ghost" onClick={() => setPicked([])} data-testid="tasks-bulk-clear">Clear</Button>
          <span className="text-xs text-muted-foreground">Bulk stage changes are in Tax &amp; BAS → Compliance tasks.</span>
        </div>)}

      {/* Kanban Board */}
      {view === 'kanban' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 2xl:grid-cols-5 gap-4">
          {statusColumns.map((column) => (
            <Card key={column.id} className={column.bgColor} data-testid={`kanban-column-${column.id}`}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
                  <span data-testid={`kanban-title-${column.id}`}>{column.id === 'done' ? doneTitle(completed) : column.title}</span>
                  <Badge variant="outline" data-testid={`kanban-count-${column.id}`}>{tasksByStatus[column.id].length}</Badge>
                </CardTitle>
                {column.id === 'done' && completed !== 'all' && (
                  <button type="button" className="text-left text-xs text-primary hover:underline" onClick={() => setUrlF({ ...urlF, completed: 'all' })} data-testid="kanban-view-all-completed">View all completed</button>)}
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {tasksByStatus[column.id].length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">No tasks</p>
                  ) : (
                    tasksByStatus[column.id].map((task) => (
                      <Card key={task.id} className="bg-card hover:shadow-md transition-shadow cursor-pointer" onClick={() => setDetail(task)} data-testid={`task-card-${task.id}`}>
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            <div className="flex items-start gap-2">
                              {canBulk && task.compliance_period_id && <span onClick={(e) => e.stopPropagation()} className="pt-0.5"><Checkbox checked={picked.includes(task.id)} onCheckedChange={(v) => setPicked(v ? [...picked, task.id] : picked.filter((x) => x !== task.id))} aria-label="Select task" data-testid={`task-select-${task.id}`} /></span>}
                              <div className="min-w-0">
                              <h4 className="font-medium text-foreground">{task.title}</h4>
                              {task.client_name && (
                                <p className="text-xs text-muted-foreground mt-1">{task.client_name}</p>
                              )}
                              </div>
                            </div>

                            {task.description && (
                              <p className="text-sm text-muted-foreground line-clamp-2">{task.description}</p>
                            )}

                            <div className="flex items-center justify-between">
                              <Badge className={priorityColors[task.priority]}>
                                {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                              </Badge>

                              {task.assignee_name && (
                                <Avatar className="w-6 h-6">
                                  <AvatarFallback className="bg-primary text-white text-xs">
                                    {getInitials(task.assignee_name)}
                                  </AvatarFallback>
                                </Avatar>
                              )}
                            </div>

                            {task.due_at && <DueDates task={task} />}
                            <TaskProgress task={task} />

                            {/* Quick status change */}
                            {canWrite && <div className="flex flex-wrap gap-1 pt-2 border-t" onClick={(e) => e.stopPropagation()}>
                              {statusColumns.filter(col => col.id !== column.id).map((targetCol) => (
                                <Button
                                  key={targetCol.id}
                                  variant="ghost"
                                  size="sm"
                                  className="text-xs"
                                  onClick={() => handleStatusChange(task, targetCol.id)}
                                  data-testid={`task-quick-${targetCol.id}-${task.id}`}
                                >
                                  → {targetCol.title}
                                </Button>
                              ))}
                            </div>}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : view === 'calendar' ? (
        /* Professional Google-Calendar-style view (Day/Week/Month/Year) */
        <EventCalendar events={calendarEvents} nounSingular="task" fullListLabel="Open task" onOpenFull={(ev) => setDetail(tasks.find((t) => t.id === ev.id))} />
      ) : (
        <TaskListView templates={templates} clients={clients} users={users} onOpen={setDetail} reloadKey={reloadKey} urlFilters={hasUrlF ? urlF : {}} meId={meId} />
      )}

      <TaskDetailDialog task={detail} users={users} role={role} meId={meId} onClose={() => setDetail(null)} onChanged={loadTasks} />
      <LodgeToCompleteDialog task={lodgeFor} onClose={() => setLodgeFor(null)} onDone={loadTasks} onOpenTask={setDetail} />
      <BulkCreateDialog open={bulkOpen} onClose={() => setBulkOpen(false)} templates={templates} clients={clients} users={users} onCreated={loadTasks} />
      <RecurringDialog open={recurOpen} onClose={() => setRecurOpen(false)} users={users} />
      <TaskTemplatesAdmin open={adminOpen} onClose={() => setAdminOpen(false)} onChanged={loadTemplates} />
    </div>
  );
}